import * as React from "react";
import {
  Table,
  Button,
  Select,
  Input,
  Row,
  Col,
  Space,
  Modal,
  message,
  Breadcrumb,
  Dropdown,
  Menu,
} from "antd";
import {
  BarChartOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { DownloadOutlined, ImportOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { util } from "../lib/Util";
import { FetchWithAuth } from "digimaker-ui/util";
import { exportToExcel } from "../lib/exportExcel";

const { Option } = Select;

interface Filter {
  course: any;
  stage: any;
  intake: string;
  status: string;
  firstname: string;
  lastname: string;
  student_no: string;
}

const emptyFilter = (): Filter => ({
  course: "",
  stage: "",
  intake: "",
  status: "",
  firstname: "",
  lastname: "",
  student_no: "",
});

const StudentList: React.FC = () => {
  const [students, setStudents] = React.useState<any[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [courses, setCourses] = React.useState<any[]>([]);
  const [agencies, setAgencies] = React.useState<any[]>([]);
  const [selected, setSelected] = React.useState<React.Key[]>([]);
  const [pageSize, setPageSize] = React.useState(20);
  const [filter, setFilter] = React.useState<Filter>(emptyFilter());
  const [sortby, setSortby] = React.useState<string>("id desc");

  React.useEffect(() => {
    FetchWithAuth(
      "content/list/course?parent=468&level=1&sortby=c.name%20asc&limit=100",
    ).then((data: any) => setCourses(data.data.list || []));

    FetchWithAuth("content/list/agency?limit=100").then((data: any) =>
      setAgencies(data.data.list || []),
    );
  }, []);

  const buildApiParams = (currentFilter: Filter, currentSortby: string) => {
    const apiFilter: Record<string, any> = {
      parent: 467,
      level: 1,
      sortby: currentSortby,
    };
    if (currentFilter.course) apiFilter["field.course"] = currentFilter.course;
    if (currentFilter.stage) apiFilter["field.stage"] = currentFilter.stage;
    if (currentFilter.intake)
      apiFilter["field.intake"] = "contain:" + currentFilter.intake;
    if (currentFilter.status) apiFilter["field.status"] = currentFilter.status;
    if (currentFilter.firstname)
      apiFilter["field.firstname"] = "contain:" + currentFilter.firstname;
    if (currentFilter.lastname)
      apiFilter["field.lastname"] = "contain:" + currentFilter.lastname;
    if (currentFilter.student_no)
      apiFilter["field.student_no"] = "contain:" + currentFilter.student_no;
    const params = new URLSearchParams();
    Object.entries(apiFilter).forEach(([k, v]) => params.append(k, String(v)));
    return params;
  };

  React.useEffect(() => {
    setLoading(true);
    const params = buildApiParams(filter, sortby);
    params.append("offset", String((page - 1) * pageSize));
    params.append("limit", String(pageSize));

    FetchWithAuth(`content/list/student?${params.toString()}`)
      .then((data: any) => {
        setStudents(data.data.list || []);
        setTotal(data.data.count || 0);
        setSelected([]);
      })
      .finally(() => setLoading(false));
  }, [filter, page, sortby, pageSize]);

  const onFilterChange = (key: keyof Filter, value: any) => {
    setPage(1);
    setFilter((f) => ({ ...f, [key]: value ?? "" }));
  };

  const download = async () => {
    const params = buildApiParams(filter, sortby);
    params.append("limit", "0");
    const data = await FetchWithAuth(
      `content/list/student?${params.toString()}`,
    );
    const list: any[] = data.data.list || [];
    list.forEach((item) => {
      item.course_name = courseMap[item.course] || "";
      const agency = agencies.find((a: any) => a.id === item.agency);
      item.agency_name = agency ? agency.name : "";
    });
    exportToExcel(
      list,
      [
        { label: "Student NO.", value: "student_no" },
        { label: "First Name", value: "firstname" },
        { label: "Last Name", value: "lastname" },
        { label: "DOB", value: (row) => row["birthdate"].split("T")[0] },
        { label: "Intake", value: "intake" },
        { label: "Course", value: "course_name" },
        { label: "Agency", value: "agency_name" },
        { label: "Stage", value: "stage" },
        { label: "Telephone", value: "telephone" },
        { label: "Sex", value: "sex" },
        { label: "Nationality", value: "nationality" },
        { label: "Email", value: "email" },
        { label: "Address 1", value: "address1" },
        { label: "Address 2", value: "address2" },
        { label: "Address 3", value: "address3" },
        { label: "Address 4", value: "address4" },
        { label: "Status", value: "status" },
      ],
      "Student List",
      "Student List",
    );
  };

  const courseMap = React.useMemo(() => {
    const m: Record<number, string> = {};
    courses.forEach((c) => (m[c.id] = c.name));
    return m;
  }, [courses]);

  const handleBulkDelete = () => {
    Modal.confirm({
      title: `Delete ${selected.length} student${selected.length > 1 ? "s" : ""}?`,
      content: "This cannot be undone.",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        let failed = 0;
        const ids = selected.join(",");
        try {
          await FetchWithAuth("content/delete?cid=" + ids + "&type=student");
        } catch {
          failed++;
        }
        if (failed > 0) {
          message.error(`${failed} deletion(s) failed.`);
        } else {
          message.success(
            `Deleted ${selected.length} student${selected.length > 1 ? "s" : ""}.`,
          );
        }
        setFilter((f) => ({ ...f }));
      },
    });
  };

  const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
    const newSortby =
      sorter.field && sorter.order
        ? `${sorter.field} ${sorter.order === "ascend" ? "asc" : "desc"}`
        : "id desc";
    if (newSortby !== sortby) {
      setSortby(newSortby);
      setPage(1);
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      sorter: true,
      width: 70,
    },
    { title: "Student No.", dataIndex: "student_no", key: "student_no" },
    { title: "First Name", dataIndex: "firstname", key: "firstname" },
    { title: "Last Name", dataIndex: "lastname", key: "lastname" },
    {
      title: "Course",
      key: "course",
      render: (r: any) => courseMap[r.course] || r.course,
    },
    { title: "Stage", dataIndex: "stage", key: "stage" },
    { title: "Intake", dataIndex: "intake", key: "intake" },
    { title: "Status", dataIndex: "status", key: "status" },
    {
      title: "Actions",
      key: "actions",
      render: (r: any) => (
        <Space size="small">
          <Link to={`/student/view/${r.id}`}>
            <EyeOutlined /> View
          </Link>
          <Link to={`/student/edit/${r.id}`}>
            <EditOutlined /> Edit
          </Link>
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="subject">
                  <Link to={`/subject/result/view/${r.id}`}>
                    <UnorderedListOutlined /> Subject records
                  </Link>
                </Menu.Item>
                <Menu.Item key="fee">
                  <Link to={`/finance/records/${r.id}`}>
                    <DollarOutlined /> Fee
                  </Link>
                </Menu.Item>
                <Menu.Item key="results">
                  <Link to={`/subject/result/view/${r.id}`}>
                    <BarChartOutlined /> Results
                  </Link>
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
          >
            <Button type="link" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <h2>Students</h2>
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link to="/">Home</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Students</Breadcrumb.Item>
      </Breadcrumb>

      <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
        <Col>
          <Select
            style={{ width: 240 }}
            placeholder="Course"
            allowClear
            onChange={(v) => onFilterChange("course", v)}
          >
            {courses.map((c) => (
              <Option key={c.id} value={c.id}>
                {c.name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Select
            style={{ width: 80 }}
            placeholder="Stage"
            allowClear
            onChange={(v) => onFilterChange("stage", v)}
          >
            {[1, 2, 3, 4].map((s) => (
              <Option key={s} value={s}>
                {s}
              </Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Select
            style={{ width: 120 }}
            placeholder="Intake"
            allowClear
            onChange={(v) => onFilterChange("intake", v)}
          >
            {util.intakes().map((i) => (
              <Option key={i} value={i}>
                {i}
              </Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Select
            style={{ width: 220 }}
            placeholder="Status"
            allowClear
            onChange={(v) => onFilterChange("status", v)}
          >
            {util.statusList.map((s) => (
              <Option key={s} value={s}>
                {s}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Row gutter={[8, 8]} style={{ marginTop: 8 }} align="middle">
        <Col>
          <Input
            style={{ width: 140 }}
            placeholder="First name"
            onChange={(e) => onFilterChange("firstname", e.target.value)}
          />
        </Col>
        <Col>
          <Input
            style={{ width: 140 }}
            placeholder="Last name"
            onChange={(e) => onFilterChange("lastname", e.target.value)}
          />
        </Col>
        <Col>
          <Input
            style={{ width: 130 }}
            placeholder="Student No."
            onChange={(e) => onFilterChange("student_no", e.target.value)}
          />
        </Col>
        <Col flex="auto" style={{ textAlign: "right" }}>
          <Space>
            <Link to="/student/create">
              <Button type="primary">
                <PlusOutlined /> Add Student
              </Button>
            </Link>
            <Link to="/import/students">
              <Button>
                <ImportOutlined /> Import
              </Button>
            </Link>
            <Button icon={<DownloadOutlined />} onClick={download}>
              Download with filter
            </Button>

            <Button
              disabled={selected.length === 0}
              danger
              icon={<DeleteOutlined />}
              onClick={handleBulkDelete}
            >
              Delete selected ({selected.length})
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        style={{ marginTop: 16 }}
        rowSelection={{
          selectedRowKeys: selected,
          onChange: (keys) => setSelected(keys),
        }}
        columns={columns}
        dataSource={students}
        rowKey="id"
        loading={loading}
        size="small"
        onChange={handleTableChange}
        pagination={{
          current: page,
          pageSize: pageSize,
          total,
          onChange: (p) => {
            setPage(p);
          },
          onShowSizeChange: (p, s) => {
            setPage(1);
            setPageSize(s);
          },
          showTotal: (t) => `${t} students`,
        }}
      />
    </div>
  );
};

export default StudentList;
